import * as i0 from '@angular/core';
import { EventEmitter, Component, Output, Input, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { coerceBooleanProperty, coerceNumberProperty } from '@roosevelt/common-ui-lib/core';
import { interval, Subject, of } from 'rxjs';
import { takeUntil, delay, tap } from 'rxjs/operators';
import { IconComponent, IconModule } from '@roosevelt/common-ui-lib/icon';

const timer = (timeout, cancel) => interval(timeout).pipe(takeUntil(cancel));

/**
 * Notification Component
 *
 * @description A notification component that comes in a few colors, pass in content
 * @example
 * <!-- rs-doc {link: 'Example of notifications all colors'} -->
 * <rs-notification type="success" [visible]="!successHidden" closeable (visibleChange)="successHidden = !event" message="Notification text goes here."></rs-notification>
 * <rs-notification type="warning" visible message="Notification text goes here."></rs-notification>
 * <rs-notification type="info" [visible]="!infoHidden" closeable (visibleChange)="infoHidden = !$event" message="Notification text goes here."></rs-notification>
 * <rs-notification type="error" visible message="Notification text goes here."></rs-notification>
 * @example
 * <!-- rs-doc {link: 'Example of complex notification'} -->
 * <rs-notification type="error" [visible]="!errorHidden" closeable (visibleChange)="errorHidden = !$event" message="Notification title">
 *  <div>
 *   <p>Subtitle text goes here. Lorem ipsum dolor sit amet con sectetuer adipiscing elit.</p>
 *   <button rs-button style="padding-top: 0; padding-left: 0; padding-bottom: 0; min-height: 20px;">Example Link</button>
 *   <button rs-button style="border-left: 2px solid lightgray; padding-top: 0; padding-bottom: 0; min-height: 20px;">Second Example Link</button>
 *  </div>
 * </rs-notification>
 * @example
 * <!-- rs-doc {link: 'Example of complex notifications'} -->
 * <rs-notification type="error" [visible]="!errorHidden" closeable (visibleChange)="errorHidden = !$event" message="Search Error">
 *  <div>
 *   <ul>
 *    <li>Error 1</li>
 *    <li>Error 2</li>
 *   </ul>
 *   <button rs-button style="padding-top: 0; padding-left: 0; padding-bottom: 0; min-height: 20px;">Report This Issue</button>
 *  </div>
 * </rs-notification>
 * @example
 * <!-- rs-doc {link: 'Example of Password Expiration notification with CustomExpirationMessage'} -->
 * <rs-notification type="warning" [checkPasswordExpiry]="!warningHidden" closeable (visibleChange)="warningHidden = !$event" customExpirationMessage="Your password will expire in {days} days. Please change your password soon.">
 *  <div notificationcontent>
 *   <button rs-button style="padding-top: 0; padding-left: 0; padding-bottom: 0; min-height: 20px;">Update Password</button>
 *  </div>
 * </rs-notification>
 * <p><b>NOTE:</b> Banner won't be shown because Okta Token is not available. </p>
 * @example
 * <!-- rs-doc {link: 'Example of Password Expiration notification without CustomExpirationMessage'} -->
 * <rs-notification type="warning" [checkPasswordExpiry]="!warningHidden" closeable (visibleChange)="warningHidden = !$event">
 *  <div notificationcontent>
 *   <button rs-button style="padding-top: 0; padding-left: 0; padding-bottom: 0; min-height: 20px;">Update Password</button>
 *  </div>
 * </rs-notification>
 * <p><b>NOTE:</b> Banner won't be shown because Okta Token is not available. </p>
 * @example
 * <!-- rs-doc {link: 'Example of an auto hide notification'} -->
 * <button rs-raised-button (click)="show = true">Show Notification For 10s</button>
 * <rs-notification type="success" [visible]="show" displayTimeout="10000" closeable (visibleChange)="show = false" message="Notification text goes here."></rs-notification>
 * @example
 * <!-- rs-doc {link: 'Example of a toast notification default position'} -->
 * <button rs-raised-button (click)="show = true">Show Toast For 10s</button>
 * <rs-notification type="success" toast [visible]="show" displayTimeout="10000" closeable (visibleChange)="show = false" message="Notification text goes here."></rs-notification>
 * @example
 * <!-- rs-doc {link: 'Example of a toast notification position set'} -->
 * <button rs-raised-button (click)="show = true">Show Toast</button>
 * <rs-notification type="success" [toast]="{left: 300, top: 125}" [visible]="show" closeable (visibleChange)="show = false" message="Notification text goes here."></rs-notification>
 */
class NotificationComponent {
    constructor() {
        /** emits when ever the visible state changes in the dialog */
        this.visibleChange = new EventEmitter();
        /** top line of the notification content */
        this.message = '';
        /** all the possible notifications */
        this.type = 'info';
        /** when passing custom as a type it allows you to configure your own color and icon */
        this.customConfig = undefined;
        /** CustomExpiryMessage for Password Expiration */
        this.customExpirationMessage = '';
        this.animateToastIn = false;
        this.cancelHide = new Subject();
        this.cancelHideSub = undefined;
        this.visibleForPasswordExpiration = false;
        this._closeable = false;
        this._checkPasswordExpiry = false;
        this._visible = false;
        this._displayTimeout = 0;
        this._toastPosition = undefined;
        this.daysLeft = 15; //Setting minimum to 15 as Password Expiration threshold is 14.
    }
    /** to hide or show the notification */
    get visible() {
        return this._visible;
    }
    set visible(value) {
        this._visible = coerceBooleanProperty(value);
        if ((this._visible && this.displayTimeout) || (this._visible && this.toast)) {
            this.setUpTimer();
        }
    }
    /** to hide or show the notification */
    get toast() {
        return this._toastPosition;
    }
    set toast(value) {
        if (!value) {
            this._toastPosition = { top: 25, right: 30 };
        }
        else {
            this._toastPosition = value;
        }
        if (this._visible && this._toastPosition) {
            this.setUpTimer();
        }
    }
    /** if you would like the notification to be close automatically after n milliseconds, set this number value */
    get displayTimeout() {
        return this._displayTimeout;
    }
    set displayTimeout(value) {
        this._displayTimeout = coerceNumberProperty(value);
        if (this._displayTimeout && this._visible) {
            this.setUpTimer();
        }
    }
    /** Boolean to check for Password Expiry */
    get checkPasswordExpiry() {
        return this._checkPasswordExpiry;
    }
    set checkPasswordExpiry(value) {
        this._checkPasswordExpiry = coerceBooleanProperty(value);
    }
    /** allows the notification to be closed */
    get closeable() {
        return this._closeable;
    }
    set closeable(value) {
        this._closeable = coerceBooleanProperty(value);
    }
    ngOnInit() {
        if (!this.toast && !this.displayTimeout) {
            this.cancelHideSub = this.cancelHide.subscribe(() => this.visibleChange.emit(false));
        }
        if (this.checkPasswordExpiry) {
            this.visibleForPasswordExpiration = this.checkForPasswordExpiration();
        }
        if (this.visibleForPasswordExpiration) {
            this.updateCustomExpirationMessage();
        }
    }
    // Function to check if password has expired. If expired, then returns true.
    checkForPasswordExpiration() {
        const today = new Date();
        let oktaSession = sessionStorage.getItem('okta-token-storage');
        let passwordExpirationTime = '';
        let userClass = '';
        if (oktaSession && oktaSession?.indexOf('idToken') > -1) {
            userClass = JSON.parse(oktaSession).idToken.claims.userClass;
            if (userClass === "Internal Customers" || userClass === "External Users") {
                passwordExpirationTime = JSON.parse(oktaSession).idToken.claims.passwordExpirationTime;
            }
        }
        if (passwordExpirationTime !== '') {
            const expiryDate = new Date(passwordExpirationTime);
            this.daysLeft = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
            if (this.daysLeft <= 14) {
                return true;
            }
        }
        return false;
    }
    // Function to set the dynamic Password Expiration Message based on daysLeft.
    updateCustomExpirationMessage() {
        this.dynamicMessage = this.customExpirationMessage ?
            this.customExpirationMessage.replace('{days}', this.daysLeft.toString()) : 'Your password will expire in ' + this.daysLeft + ' days. Please change your password as soon as possible.';
    }
    setUpTimer() {
        // push the show to the bottom of the stack so that it will render the animation
        of('').pipe(delay(0)).subscribe(() => this.animateToastIn = true);
        if (!this.cancelHideSub) {
            // when the toast is hidden we need to delay to show the out animtation
            this.cancelHideSub = this.cancelHide.pipe(tap(() => this.animateToastIn = false), delay(500)).subscribe(() => this.visibleChange.emit(false));
        }
        return timer(this.displayTimeout || 999999, this.cancelHide).subscribe(() => this.cancelHide.next(false));
    }
    /* eslint-disable */
    static { this.ngAcceptInputType_displayTimeout = undefined; }
    static { this.ngAcceptInputType_visible = undefined; }
    static { this.ngAcceptInputType_closeable = undefined; }
    static { this.ngAcceptInputType_toast = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: NotificationComponent, isStandalone: true, selector: "rs-notification", inputs: { visible: "visible", toast: "toast", displayTimeout: "displayTimeout", message: "message", type: "type", customConfig: "customConfig", checkPasswordExpiry: "checkPasswordExpiry", customExpirationMessage: "customExpirationMessage", closeable: "closeable" }, outputs: { visibleChange: "visibleChange" }, ngImport: i0, template: "@if (visible) {\n  <div [class]=\"type + ' notification'\" [class.toast]=\"!!toast\" [class.from-left]=\"!!toast && !!toast.left\"\n    [style.top.px]=\"toast?.top\" [style.right.px]=\"toast?.right\" [style.bottom.px]=\"toast?.bottom\" [style.left.px]=\"toast?.left\" [style.z-index]=\"toast?.z\"\n    [class.show]=\"animateToastIn\"\n    [style.border]=\"type !== 'custom' ? undefined : customConfig.color + ' solid 1px'\">\n    @switch (type) {\n      <!-- success -->\n      @case ('success') {\n        <rs-icon style=\"color: #338000;\">complete</rs-icon>\n      }\n      <!-- warning -->\n      @case ('warning') {\n        <rs-icon style=\"color: #A36A00;\">warning</rs-icon>\n      }\n      <!-- info -->\n      @case ('info') {\n        <rs-icon style=\"color: #0068F0;\">info</rs-icon>\n      }\n      <!-- error -->\n      @case ('error') {\n        <rs-icon style=\"color: #a94442;\">error</rs-icon>\n      }\n      <!-- custom -->\n      @case ('custom') {\n        <rs-icon [style.color]=\"customConfig.color\">{{customConfig.icon}}</rs-icon>\n      }\n    }\n    <div class=\"notification-content\">\n      @if (message) {\n        <h3>{{message}}</h3>\n      }       \n      <ng-content></ng-content>\n    </div>\n    @if (closeable) {\n      <rs-icon (click)=\"cancelHide.next(false)\" style=\"cursor: pointer;\">close</rs-icon>\n    }\n  </div>\n}\n\n@if (checkPasswordExpiry && visibleForPasswordExpiration) {\n  <div [class]=\"type + ' notification'\">\n    @switch (type) {\n      <!-- warning -->\n      @case ('warning') {\n        <rs-icon class = \"warning\">warning</rs-icon>\n      }\n      <!-- error -->\n      @case ('error') {\n        <rs-icon class = \"error\">error</rs-icon>\n      }\n      <!-- custom -->\n      @case ('custom') {\n        <rs-icon [style.color]=\"customConfig.color\">{{customConfig.icon}}</rs-icon>\n      }\n    }\n    <div class=\"notification-content\">\n        <h3>{{dynamicMessage}}</h3>\n      <ng-content select=\"[notificationcontent]\"></ng-content>\n    </div>\n    @if (closeable) {\n      <rs-icon (click)=\"cancelHide.next(false)\" class = \"close-icon\">close</rs-icon>\n    }\n  </div>\n}\n", styles: [".notification{letter-spacing:1.5px;padding:10px;border-radius:5px;font-size:1em;margin:10px;display:flex;background-color:#fff}.notification.toast{position:fixed;opacity:0;transform:translate(calc(100% + 40px));transition:all .5s cubic-bezier(.68,-.55,.25,1.35)}.notification.toast.from-left{transform:translate(calc(-100% - 40px))}.notification.toast.show{opacity:1;transform:translate(0)}.notification.success{border:1px solid #6db04d}.notification.warning{border:1px solid #ed9c0b}.notification.info{border:1px solid #358dff}.notification.error{border:1px solid #a94442}.notification .notification-content{flex:1;padding:0 10px}.notification .notification-content h3{margin:3px 0}.notification .error{color:#a94442}.notification .info{color:#358dff}.notification .warning{color:#ed9c0b}.notification .success{color:#6db04d}.notification .close-icon{cursor:pointer}\n"], dependencies: [{ kind: "component", type: IconComponent, selector: "rs-icon" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-notification', standalone: true, imports: [IconComponent], template: "@if (visible) {\n  <div [class]=\"type + ' notification'\" [class.toast]=\"!!toast\" [class.from-left]=\"!!toast && !!toast.left\"\n    [style.top.px]=\"toast?.top\" [style.right.px]=\"toast?.right\" [style.bottom.px]=\"toast?.bottom\" [style.left.px]=\"toast?.left\" [style.z-index]=\"toast?.z\"\n    [class.show]=\"animateToastIn\"\n    [style.border]=\"type !== 'custom' ? undefined : customConfig.color + ' solid 1px'\">\n    @switch (type) {\n      <!-- success -->\n      @case ('success') {\n        <rs-icon style=\"color: #338000;\">complete</rs-icon>\n      }\n      <!-- warning -->\n      @case ('warning') {\n        <rs-icon style=\"color: #A36A00;\">warning</rs-icon>\n      }\n      <!-- info -->\n      @case ('info') {\n        <rs-icon style=\"color: #0068F0;\">info</rs-icon>\n      }\n      <!-- error -->\n      @case ('error') {\n        <rs-icon style=\"color: #a94442;\">error</rs-icon>\n      }\n      <!-- custom -->\n      @case ('custom') {\n        <rs-icon [style.color]=\"customConfig.color\">{{customConfig.icon}}</rs-icon>\n      }\n    }\n    <div class=\"notification-content\">\n      @if (message) {\n        <h3>{{message}}</h3>\n      }       \n      <ng-content></ng-content>\n    </div>\n    @if (closeable) {\n      <rs-icon (click)=\"cancelHide.next(false)\" style=\"cursor: pointer;\">close</rs-icon>\n    }\n  </div>\n}\n\n@if (checkPasswordExpiry && visibleForPasswordExpiration) {\n  <div [class]=\"type + ' notification'\">\n    @switch (type) {\n      <!-- warning -->\n      @case ('warning') {\n        <rs-icon class = \"warning\">warning</rs-icon>\n      }\n      <!-- error -->\n      @case ('error') {\n        <rs-icon class = \"error\">error</rs-icon>\n      }\n      <!-- custom -->\n      @case ('custom') {\n        <rs-icon [style.color]=\"customConfig.color\">{{customConfig.icon}}</rs-icon>\n      }\n    }\n    <div class=\"notification-content\">\n        <h3>{{dynamicMessage}}</h3>\n      <ng-content select=\"[notificationcontent]\"></ng-content>\n    </div>\n    @if (closeable) {\n      <rs-icon (click)=\"cancelHide.next(false)\" class = \"close-icon\">close</rs-icon>\n    }\n  </div>\n}\n", styles: [".notification{letter-spacing:1.5px;padding:10px;border-radius:5px;font-size:1em;margin:10px;display:flex;background-color:#fff}.notification.toast{position:fixed;opacity:0;transform:translate(calc(100% + 40px));transition:all .5s cubic-bezier(.68,-.55,.25,1.35)}.notification.toast.from-left{transform:translate(calc(-100% - 40px))}.notification.toast.show{opacity:1;transform:translate(0)}.notification.success{border:1px solid #6db04d}.notification.warning{border:1px solid #ed9c0b}.notification.info{border:1px solid #358dff}.notification.error{border:1px solid #a94442}.notification .notification-content{flex:1;padding:0 10px}.notification .notification-content h3{margin:3px 0}.notification .error{color:#a94442}.notification .info{color:#358dff}.notification .warning{color:#ed9c0b}.notification .success{color:#6db04d}.notification .close-icon{cursor:pointer}\n"] }]
        }], propDecorators: { visibleChange: [{
                type: Output
            }], visible: [{
                type: Input
            }], toast: [{
                type: Input
            }], displayTimeout: [{
                type: Input
            }], message: [{
                type: Input
            }], type: [{
                type: Input
            }], customConfig: [{
                type: Input
            }], checkPasswordExpiry: [{
                type: Input
            }], customExpirationMessage: [{
                type: Input
            }], closeable: [{
                type: Input
            }] } });

class NotificationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: NotificationModule, imports: [CommonModule,
            IconModule,
            NotificationComponent], exports: [NotificationComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationModule, imports: [CommonModule,
            IconModule,
            NotificationComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NotificationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule,
                        NotificationComponent
                    ],
                    exports: [NotificationComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { NotificationComponent, NotificationModule };
